import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Box, Typography, Grid, useTheme, useMediaQuery, Container } from '@mui/material';
import LeftPlusButton from '../../../UIControls/LeftPlusButton';
import CircleWithPlusWhite from '../../../../assets/icons/CircleWithPlusWhite.svg';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import UserApi from '../../../../rest-api/user-api';
import { useSnackbar } from '../../SnackbarProvider';
import AddPatientDialog from './add-patient-modal';
import PaginationControls from '../../../UIControls/PaginationControls';
import PatientRow from '../../../UIControls/PatientRow';
import { FeedbackCTA } from '../../feedback-cta';
import GradientBanner from '../../../UIControls/GradientBanner';
import BlockingSpinner from '../../../UIControls/BlockingSpinner';

const MyClinic = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { authStore, whiteboardStore } = useContext(StoreContext);
    const [patients, setPatients] = useState([]);
    // This state will always hold the filtered list.
    const [filteredPatients, setFilteredPatients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [patientsPerPage, setPatientsPerPage] = useState(10);
    const [sortField, setSortField] = useState('');
    const [sortAsc, setSortAsc] = useState(true);

    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingPatient, setEditingPatient] = useState(null);

    const selectedRef = useRef();
    const [selectedFromStorage, setSelectedFromStorage] = useState(null);
    // keep returnToMyClinic
    const returnStateRef = useRef(null);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const showSnackbar = useSnackbar();
    const [loading, setLoading] = useState(true);

    // check for returnToMyClinic (search phrase and selected patient).
    useEffect(() => {
        const returnToClinicState = sessionStorage.getItem('returnToMyClinic');
        if (returnToClinicState) {
            returnStateRef.current = JSON.parse(returnToClinicState);
            sessionStorage.removeItem('returnToMyClinic');
        }
        fetchPatients();
    }, []);

    // keep selected for 4 so clear it after 4 sec
    useEffect(() => {
        if (selectedFromStorage) {
            const timeout = setTimeout(() => setSelectedFromStorage(null), 4000);
            return () => clearTimeout(timeout);
        }
    }, [selectedFromStorage]);

    // Fetch the full patient list and store it.
    const fetchPatients = async () => {
        try {
            const token = authStore.idToken;
            const response = await UserApi.getPatientsByFilter(token); // TODO I am hasiteted to keep in in local storage (specialy for Physio if replicated reg GDPR)
            const data = response.patients || [];
            setPatients(data);
        } catch (err) {
            console.error('Failed to load patients', err);
            showSnackbar('Failed to load patients.', 'error');
            setPatients([]);
        } finally {
            setLoading(false);
        }
    };

    // filtering patients ( the full list or search term changes)
    useEffect(() => {
        if (patients.length === 0) {
            setFilteredPatients([]);
            return;
        }

        let filtered = patients;
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = patients.filter((p) =>
                [
                    'patientName',
                    'clientSurname',
                    'microchipNumber',
                    'clinicPatientId',
                    'species',
                    'breed',
                    'patientStatus',
                ].some((field) => p[field]?.toLowerCase().includes(lowerSearch)),
            );
        }
        setFilteredPatients(filtered);
        setPage(0);
    }, [patients, searchTerm]);

    // If returnStateRef exists, update the search term and selected patient (trigger the filtering..)
    useEffect(() => {
        if (patients.length && returnStateRef.current) {
            const { selectedPatientId, searchPhrase } = returnStateRef.current;
            setSearchTerm(searchPhrase || '');
            setSelectedFromStorage(selectedPatientId || null);
            // scroll the selected patient
            setTimeout(() => {
                selectedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 200);
            returnStateRef.current = null;
        }
    }, [patients]);

    const handleSearchInputChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
    };

    const handleArchive = async (patientId) => {
        try {
            await UserApi.createOrUpdatePatient(authStore.idToken, {
                patientId,
                patientStatus: 'archived',
            });
            showSnackbar('Patient archived');
            fetchPatients();
        } catch (err) {
            showSnackbar('Failed to archive patient', 'error');
        }
    };

    const handleEditPatient = (patient) => {
        setEditingPatient(patient);
        setEditDialogOpen(true);
    };

    const sortData = (field) => {
        const isSameField = sortField === field;
        const asc = isSameField ? !sortAsc : true;
        const sorted = [...filteredPatients].sort((a, b) => {
            const valA = (a[field] || '').toString().toLowerCase();
            const valB = (b[field] || '').toString().toLowerCase();
            return asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
        });
        setSortField(field);
        setSortAsc(asc);
        setFilteredPatients(sorted);
    };

    const paginatedPatients = filteredPatients.slice(page * patientsPerPage, (page + 1) * patientsPerPage);

    const columns = isMobileOrTablet
        ? []
        : [
              { key: 'patientName', label: 'Name' },
              { key: 'clientSurname', label: 'Surname' },
              { key: 'DOB', label: 'DOB' },
              { key: 'microchipNumber', label: 'Microchip' },
              { key: 'species', label: 'Species' },
              { key: 'breed', label: 'Breed' },
              { key: 'clinicPatientId', label: 'PMS ID' },
              { key: 'patientStatus', label: 'Status' },
              { key: 'actions', label: 'Operation' },
              { key: 'whiteboards', label: 'Whiteboards' },
          ];

    return (
        <>
            {loading && <BlockingSpinner open={true} />}
            <GradientBanner
                title="My Clinic"
                subtitle="Overview of all patients in your clinic"
                placeholder="Search patients..."
                searchTerm={searchTerm}
                onSearchChange={handleSearchInputChange}
                isMobile={isMobile}
                isTablet={isMobileOrTablet}
                height={isMobile ? 'initial' : '450px'}
                className="search-banner"
            />

            {/* Main Panel */}
            <Container
                component={'section'}
                maxWidth={'xl'}
                sx={{ backgroundColor: '#F0F0FC', px: isMobileOrTablet ? 2 : 6, pt: 3 }}
                className="clinic-table"
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box display="flex" alignItems="center" gap={8} sx={{ mt: '20px', mb: '20px' }}>
                        <LeftPlusButton
                            label="New Patient"
                            icon={CircleWithPlusWhite}
                            onClick={() => setAddDialogOpen(true)}
                            iconGap={60}
                            isMobileOrTablet={isMobileOrTablet}
                        />
                    </Box>
                </Box>

                {/* Table */}
                <Box component="section" sx={{ backgroundColor: 'white', borderRadius: 2, overflowX: 'auto' }}>
                    {/* Simplified header that uses only the columns array */}
                    {!isMobileOrTablet && (
                        <Grid container sx={{ height: '60px', px: 2, minWidth: 1100 }}>
                            {columns.map((col) => (
                                <Grid
                                    item
                                    key={col.key}
                                    xs={col.key === 'microchipNumber' ? 2 : col.key === 'breed' ? 2 : 1}
                                    sx={{ display: 'flex', alignItems: 'center' }}
                                    onClick={() =>
                                        col.key !== 'actions' && col.key !== 'whiteboards' ? sortData(col.key) : null
                                    }
                                >
                                    <Typography>
                                        {col.label}
                                        {sortField === col.key && (sortAsc ? ' ▲' : ' ▼')}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    {/* Records remain the same */}
                    {!loading && paginatedPatients.length === 0 ? (
                        <Typography sx={{ mt: 3, mb: 4, textAlign: 'center', color: '#000453', px: 2 }}>
                            No patients registered yet
                        </Typography>
                    ) : (
                        paginatedPatients.map((p, index) => {
                            const isSelected = p.patientId === selectedFromStorage;
                            const whiteboardCount = Object.values(whiteboardStore.whiteboardData).filter(
                                (wb) => wb.patientId === p.patientId && wb.whiteboardStatus !== 'archived',
                            ).length;
                            const isFirstItem = index === 0;
                            const isLastItem = index === paginatedPatients.length - 1;

                            return (
                                <PatientRow
                                    key={p.patientId}
                                    patient={p}
                                    selectedRef={selectedRef}
                                    isSelected={isSelected}
                                    isMobileOrTablet={isMobileOrTablet}
                                    isFirstItem={isFirstItem}
                                    isLastItem={isLastItem}
                                    hasWhiteboard={whiteboardCount > 0}
                                    whiteboardCount={whiteboardCount}
                                    onEdit={handleEditPatient}
                                    onArchive={handleArchive}
                                    onView={() => {
                                        sessionStorage.setItem(
                                            'returnToMyClinic',
                                            JSON.stringify({
                                                selectedPatientId: p.patientId,
                                                searchPhrase: searchTerm,
                                            }),
                                        );
                                        sessionStorage.setItem('myClinicSelectedPatient', JSON.stringify(p));
                                        navigate(`/my-clinic-whiteboard/${p.patientId}`);
                                    }}
                                />
                            );
                        })
                    )}
                </Box>

                {/* Pagination */}
                <PaginationControls
                    page={page}
                    setPage={setPage}
                    totalPages={Math.ceil(filteredPatients.length / patientsPerPage)}
                    pageSize={patientsPerPage}
                    setPageSize={(value) => {
                        setPage(0);
                        setPatientsPerPage(value);
                    }}
                />
            </Container>
            <AddPatientDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                showSnackbar={showSnackbar}
                onSubmit={async (newPatientData) => {
                    try {
                        await UserApi.createOrUpdatePatient(authStore.idToken, newPatientData);
                        showSnackbar('New patient added!', 'success');
                        fetchPatients(); // refresh list
                    } catch (error) {
                        console.error('Failed to add patient:', error);
                        showSnackbar('Failed to add patient.', 'error');
                    }
                }}
            />

            {editingPatient && (
                <AddPatientDialog
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    showSnackbar={showSnackbar}
                    onSubmit={async (updatedData) => {
                        try {
                            await UserApi.createOrUpdatePatient(authStore.idToken, {
                                ...updatedData,
                                patientId: editingPatient.patientId,
                            });
                            showSnackbar('Patient updated!', 'success');
                            fetchPatients();
                        } catch (error) {
                            console.error('Failed to update patient:', error);
                            showSnackbar('Failed to update patient.', 'error');
                        } finally {
                            setEditDialogOpen(false);
                        }
                    }}
                    initialValues={editingPatient}
                />
            )}

            {/* New Feedback Section */}
            <FeedbackCTA />
        </>
    );
};

export default observer(MyClinic);
